import { useMemo, useState } from 'react';
import {
  BarChart3,
  Bell,
  Car,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  CreditCard,
  ExternalLink,
  FileText,
  Fuel,
  HelpCircle,
  KeyRound,
  Lightbulb,
  LifeBuoy,
  Mail,
  MessageCircle,
  Search,
  Settings as SettingsIcon,
  ShieldAlert,
  Smartphone,
  Sparkles,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';

interface Topic {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  category: 'comecar' | 'frota' | 'operacao' | 'admin';
  description: string;
  link?: { to: string; label: string };
  /** Conteúdo completo do tópico (markdown-like simples). */
  body: TopicBlock[];
}

type TopicBlock =
  | { type: 'p'; text: string }
  | { type: 'h'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'tip'; text: string };

const CATEGORY_LABEL: Record<Topic['category'], string> = {
  comecar: 'Para começar',
  frota: 'Frota',
  operacao: 'Operação',
  admin: 'Administração',
};

// ---- Conteúdo de ajuda --------------------------------------------------

const TOPICS: Topic[] = [
  {
    id: 'primeiros-passos',
    icon: Sparkles,
    title: 'Primeiros passos',
    category: 'comecar',
    description:
      'O caminho mais rápido pra começar a operar: cadastrar veículos, motoristas e o primeiro checklist.',
    body: [
      { type: 'h', text: 'Sequência recomendada' },
      {
        type: 'ol',
        items: [
          'Cadastre seus veículos em "Veículos"',
          'Cadastre os motoristas em "Motoristas" e marque "Criar acesso ao aplicativo" se eles forem usar o app',
          'Vincule cada motorista a um veículo (aba "Veículos" na tela do motorista)',
          'Crie um modelo de checklist em "Checklists"',
          'No celular, o motorista faz login com email/senha e executa o checklist',
        ],
      },
      {
        type: 'tip',
        text: 'O cartão "Primeiros passos" do Dashboard acompanha automaticamente seu progresso e some quando tudo estiver pronto.',
      },
    ],
  },
  {
    id: 'app-motorista',
    icon: Smartphone,
    title: 'Aplicativo do motorista',
    category: 'comecar',
    description:
      'Como o motorista usa o app pra fazer checklist e registrar abastecimento.',
    body: [
      { type: 'h', text: 'O que o motorista faz no app' },
      {
        type: 'ul',
        items: [
          'Faz login com email + senha (definidos quando você cadastrou o motorista)',
          'O veículo aparece automaticamente no topo da tela com a placa em destaque',
          'Se tiver mais de um veículo vinculado, ele troca pelo botão "TROCAR"',
          'Faz checklist item a item (OK / NOK / N/A) com observação opcional',
          'Registra abastecimento com litros, R$/L, hodômetro e foto do cupom',
        ],
      },
      {
        type: 'h',
        text: 'Se o motorista esqueceu a senha',
      },
      {
        type: 'p',
        text: 'Vá em Usuários, clique no menu ⋯ do motorista e escolha "Redefinir senha". O sistema envia um aviso por email, encerra todas as sessões ativas e você anota a nova senha pra repassar pra ele.',
      },
    ],
  },
  {
    id: 'veiculos',
    icon: Car,
    title: 'Veículos',
    category: 'frota',
    description:
      'Cadastro de veículos, documentos do CRLV e vínculo com motoristas.',
    link: { to: '/app/vehicles', label: 'Abrir Veículos' },
    body: [
      { type: 'h', text: 'O que você pode fazer' },
      {
        type: 'ul',
        items: [
          'Cadastrar placa, marca, modelo, ano, tipo de combustível e capacidade do tanque',
          'Anexar documentos (CRLV e outros) na aba "Documentos"',
          'Ver o histórico de motoristas que dirigiram aquele veículo na aba "Motoristas"',
          'Vincular ou desvincular motorista clicando no botão correspondente',
        ],
      },
      {
        type: 'tip',
        text: 'Quando um motorista é vinculado a um veículo que já tinha outro motorista ativo, o vínculo anterior é encerrado automaticamente.',
      },
    ],
  },
  {
    id: 'motoristas',
    icon: Users,
    title: 'Motoristas',
    category: 'frota',
    description:
      'Cadastro, CNH, pontos e criação de acesso ao aplicativo.',
    link: { to: '/app/drivers', label: 'Abrir Motoristas' },
    body: [
      { type: 'h', text: 'Criar acesso ao app no momento do cadastro' },
      {
        type: 'p',
        text: 'Ao cadastrar um motorista, marque "Criar acesso ao aplicativo" e defina uma senha inicial. Isso cria automaticamente um usuário com papel "Motorista" vinculado ao motorista cadastrado.',
      },
      { type: 'h', text: 'Vínculo motorista ↔ veículo' },
      {
        type: 'p',
        text: 'Na aba "Veículos" do motorista, você associa em qual veículo ele opera. Sem esse vínculo o motorista não consegue iniciar checklist nem registrar abastecimento no app.',
      },
    ],
  },
  {
    id: 'checklists',
    icon: ClipboardCheck,
    title: 'Checklists',
    category: 'operacao',
    description:
      'Modelos de inspeção e histórico de execuções com filtro por placa e motorista.',
    link: { to: '/app/checklists', label: 'Abrir Checklists' },
    body: [
      { type: 'h', text: 'Criar um modelo' },
      {
        type: 'ul',
        items: [
          'Clique em "Novo modelo" na aba "Modelos"',
          'Defina nome, categoria (inspeção diária, semanal etc.) e os itens',
          'Cada item tem tipo (OK/NOK, OK/NOK/N/A, texto, número, foto) e pode ser obrigatório',
          'Use grupos (ex.: "Pneus", "Motor") para organizar a inspeção',
        ],
      },
      { type: 'h', text: 'Ver e filtrar execuções' },
      {
        type: 'p',
        text: 'A aba "Execuções" mostra cada inspeção feita com placa do veículo e quem executou. Use a busca para filtrar por placa, nome do motorista ou nome do modelo.',
      },
    ],
  },
  {
    id: 'abastecimento',
    icon: Fuel,
    title: 'Abastecimento',
    category: 'operacao',
    description:
      'Registros de abastecimento com cálculo automático de consumo e detecção de anomalias.',
    link: { to: '/app/fueling', label: 'Abrir Abastecimento' },
    body: [
      { type: 'h', text: 'Como o consumo é calculado' },
      {
        type: 'p',
        text: 'O sistema calcula automaticamente km/L sempre que houver dois abastecimentos com tanque cheio do mesmo veículo. A diferença de hodômetro dividida pelos litros do segundo abastecimento gera o consumo.',
      },
      { type: 'h', text: 'Anomalias detectadas' },
      {
        type: 'ul',
        items: [
          'Consumo muito acima ou abaixo da média histórica',
          'Preço por litro fora do padrão do posto',
          'Abastecimentos muito frequentes em curto período',
        ],
      },
      {
        type: 'tip',
        text: 'O dashboard tem um card "Anomalias de abastecimento" que aparece sempre que houver registros suspeitos no período.',
      },
    ],
  },
  {
    id: 'manutencao',
    icon: Wrench,
    title: 'Manutenção',
    category: 'operacao',
    description:
      'Planos preventivos por km/dias e ordens de serviço com workflow completo.',
    link: { to: '/app/maintenance', label: 'Abrir Manutenção' },
    body: [
      { type: 'h', text: 'Planos preventivos' },
      {
        type: 'p',
        text: 'Defina periodicidade por quilometragem, dias ou ambos. O sistema avisa quando estiver próximo do vencimento ou quando passar do prazo.',
      },
      { type: 'h', text: 'Ordens de serviço (workflow)' },
      {
        type: 'ol',
        items: [
          'Rascunho — em criação',
          'Aprovada — admin/gestor liberou execução',
          'Em andamento — oficina trabalhando',
          'Concluída — finalizada (recalcula o próximo vencimento do plano)',
        ],
      },
    ],
  },
  {
    id: 'multas',
    icon: ShieldAlert,
    title: 'Multas',
    category: 'operacao',
    description:
      'Cadastro de multas, indicação de condutor ou empresa assume a responsabilidade.',
    link: { to: '/app/fines', label: 'Abrir Multas' },
    body: [
      { type: 'h', text: 'Responsabilidade pela multa' },
      {
        type: 'ul',
        items: [
          'Empresa: a frota arca com o valor (não há transferência de pontos)',
          'Motorista: indicar condutor transfere os pontos para a CNH dele',
        ],
      },
      { type: 'h', text: 'Workflow' },
      {
        type: 'p',
        text: 'pending → driver_indicated (quando você indica) → paid ou cancelled. A qualquer momento o gestor pode usar "Empresa assume" pra reverter a indicação.',
      },
    ],
  },
  {
    id: 'relatorios',
    icon: BarChart3,
    title: 'Relatórios e fechamento',
    category: 'admin',
    description:
      'Fechamento financeiro do período com export CSV, Excel e impressão.',
    link: { to: '/app/reports', label: 'Abrir Relatórios' },
    body: [
      { type: 'h', text: 'Como funciona' },
      {
        type: 'p',
        text: 'Escolha um preset (mês atual, anterior, trimestre, ano) ou um período personalizado. O relatório agrega abastecimento, manutenção concluída e multas pagas no período por veículo.',
      },
      { type: 'h', text: 'Exportar' },
      {
        type: 'ul',
        items: [
          'CSV — separador ";" e BOM para abrir direto no Excel',
          'Excel — arquivo .xlsx formatado com linha de totais',
          'Imprimir — gera versão limpa (sem menu) pra papel ou PDF do navegador',
        ],
      },
    ],
  },
  {
    id: 'usuarios-permissoes',
    icon: UserCog,
    title: 'Usuários e permissões',
    category: 'admin',
    description:
      'Convidar gestores, motoristas e oficina; redefinir senha e desativar acessos.',
    link: { to: '/app/users', label: 'Abrir Usuários' },
    body: [
      { type: 'h', text: 'Papéis disponíveis' },
      {
        type: 'ul',
        items: [
          'Administrador: tudo, incluindo cobrança e usuários',
          'Gestor: opera a frota mas não mexe em cobrança',
          'Motorista: usa o app, faz checklist e abastecimento',
          'Oficina: visualiza e atualiza ordens de serviço',
        ],
      },
      { type: 'h', text: 'Redefinir senha' },
      {
        type: 'p',
        text: 'Menu ⋯ → "Redefinir senha". Você digita a nova senha, o sistema envia aviso por email, encerra todas as sessões ativas e o usuário precisa fazer login com a nova senha.',
      },
    ],
  },
  {
    id: 'alertas-notificacoes',
    icon: Bell,
    title: 'Notificações e alertas',
    category: 'admin',
    description:
      'Configure quem é avisado quando CNH/CRLV vence ou manutenção atrasa.',
    link: { to: '/app/settings', label: 'Abrir Configurações' },
    body: [
      { type: 'h', text: 'Tipos de alerta' },
      {
        type: 'ul',
        items: [
          'CNH vencendo: X dias antes do vencimento',
          'CRLV vencendo: X dias antes do licenciamento',
          'Manutenção próxima/atrasada: por km ou data do plano',
          'Anomalia de abastecimento: consumo/preço fora do padrão',
          'Checklist com NOK: itens reprovados na inspeção',
        ],
      },
      { type: 'h', text: 'Canais e destinatários' },
      {
        type: 'p',
        text: 'Cada regra pode ir por painel (sininho), email, ou ambos. Você escolhe quais papéis recebem (admin, gestor, motorista).',
      },
    ],
  },
  {
    id: 'cobranca',
    icon: CreditCard,
    title: 'Cobrança e plano',
    category: 'admin',
    description:
      'Veja quanto você paga por mês (preço × veículos ativos) e o dia do fechamento.',
    link: { to: '/app/settings', label: 'Abrir Configurações' },
    body: [
      { type: 'h', text: 'Modelo de cobrança' },
      {
        type: 'p',
        text: 'Sem mensalidade fixa. Você paga apenas pelos veículos ativos cadastrados, multiplicado pelo valor unitário definido pelo administrador do sistema.',
      },
      {
        type: 'tip',
        text: 'Veículos inativos (marcados como inativos) não são cobrados. Use esse recurso quando um caminhão sair temporariamente da frota.',
      },
    ],
  },
  {
    id: 'configuracoes',
    icon: SettingsIcon,
    title: 'Dados da empresa e preferências',
    category: 'admin',
    description:
      'Atualizar dados do tenant, fuso horário e tema visual.',
    link: { to: '/app/settings', label: 'Abrir Configurações' },
    body: [
      {
        type: 'p',
        text: 'Em Configurações → aba Empresa, atualize nome fantasia, razão social, e-mail e telefone. CNPJ é imutável (definido na criação da conta).',
      },
      {
        type: 'p',
        text: 'Em Configurações → aba Preferências, escolha tema claro/escuro/sistema (salva apenas neste navegador).',
      },
    ],
  },
];

const FAQ = [
  {
    q: 'Esqueci minha senha. Como faço?',
    a: 'Na tela de login clique em "Esqueci minha senha". Você recebe um link por e-mail (válido por 1 hora) para definir uma nova senha. Se for um motorista que não tem acesso ao email, peça ao administrador para redefinir a senha pelo menu Usuários.',
  },
  {
    q: 'Como adiciono um motorista que vai usar o app no celular?',
    a: 'Em "Motoristas" → "Novo motorista", preencha os dados e marque o checkbox "Criar acesso ao aplicativo" no final do formulário. Defina uma senha inicial de no mínimo 10 caracteres e repasse-a pro motorista.',
  },
  {
    q: 'O motorista não consegue ver o veículo no app',
    a: 'Verifique 3 coisas: (1) o usuário do motorista está vinculado a um registro de motorista (Driver.userId); (2) existe um vínculo motorista↔veículo ativo (aba Veículos do motorista, sem data de encerramento); (3) o veículo está marcado como ativo. Sem esses três, aparece o aviso amarelo "Nenhum veículo vinculado".',
  },
  {
    q: 'Como exporto o relatório financeiro?',
    a: 'Em "Relatórios", escolha o período (preset ou personalizado) e use os botões "CSV", "Excel" ou "Imprimir" no canto superior direito. O Excel inclui linha de totais; o CSV tem separador ";" e BOM compatível com Excel pt-BR.',
  },
  {
    q: 'Posso ter uma cobrança diferente por cliente?',
    a: 'Sim, o valor por veículo é configurado por tenant no campo pricePerVehicleCents. Hoje a alteração desse valor é feita pela administração do SaaS, não pelo cliente final.',
  },
  {
    q: 'Como o consumo (km/L) é calculado?',
    a: 'Só calcula quando há dois abastecimentos sucessivos do mesmo veículo, ambos com "Tanque cheio" marcado. A diferença de hodômetro entre eles dividida pelos litros do segundo abastecimento dá o km/L.',
  },
  {
    q: 'O que é uma "anomalia" no abastecimento?',
    a: 'O sistema marca como anomalia quando o consumo ou preço fica muito acima/abaixo do histórico do veículo, ou quando há abastecimentos muito próximos no tempo. Aparecem com badge laranja na lista e contam no card de "Atenção" do dashboard.',
  },
  {
    q: 'Como funciona a indicação de condutor numa multa?',
    a: 'Quando você indica um motorista, a multa muda para status "driver_indicated" e o sistema registra a data e um prazo opcional. A indicação transfere os pontos da CNH para o motorista indicado. Você pode reverter a qualquer momento usando "Empresa assume".',
  },
];

// ---- Componente ----------------------------------------------------------

export function HelpPage() {
  const [query, setQuery] = useState('');
  const [openTopicId, setOpenTopicId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TOPICS;
    return TOPICS.filter((t) => {
      const haystack = [
        t.title,
        t.description,
        ...t.body.flatMap((b) =>
          b.type === 'p' || b.type === 'h' || b.type === 'tip'
            ? [b.text]
            : b.items,
        ),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const byCategory = useMemo(() => {
    const groups: Record<Topic['category'], Topic[]> = {
      comecar: [],
      frota: [],
      operacao: [],
      admin: [],
    };
    filtered.forEach((t) => groups[t.category].push(t));
    return groups;
  }, [filtered]);

  const filteredFaq = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ.filter(
      (f) =>
        f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div className="page">
      <header className="space-y-1.5">
        <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <LifeBuoy className="size-3.5" /> Administração
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Ajuda do sistema</h1>
        <p className="text-sm text-muted-foreground">
          Guia rápido de cada módulo, perguntas frequentes e canais de suporte.
        </p>
      </header>

      {/* Busca */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar na ajuda... ex.: senha, motorista, relatório, anomalia"
          className="pl-9"
        />
      </div>

      {/* Cards de canais de suporte */}
      <section className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Mail className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Suporte por e-mail</p>
              <p className="truncate text-xs text-muted-foreground">
                suporte@iabordo.com.br
              </p>
              <p className="text-[11px] text-muted-foreground">
                Resposta em até 1 dia útil
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-md bg-success/10 text-success">
              <MessageCircle className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Atendimento por telefone</p>
              <p className="text-xs text-muted-foreground">
                Falar diretamente com nosso time
              </p>
              <p className="text-[11px] text-muted-foreground">
                Seg a Sex · 9h às 18h
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-md bg-warning/15 text-warning-foreground">
              <FileText className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Documentação técnica</p>
              <p className="text-xs text-muted-foreground">
                API Swagger e termos de uso
              </p>
              <Button
                asChild
                variant="link"
                size="sm"
                className="h-auto p-0 text-[11px]"
              >
                <a
                  href="http://localhost:3000/api/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir Swagger
                  <ExternalLink className="ml-1 size-3" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Tópicos por categoria */}
      <section className="space-y-6">
        {(['comecar', 'frota', 'operacao', 'admin'] as const).map((cat) => {
          const items = byCategory[cat];
          if (items.length === 0) return null;
          return (
            <div key={cat} className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABEL[cat]}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {items.map((t) => {
                  const isOpen = openTopicId === t.id;
                  const Icon = t.icon;
                  return (
                    <Card key={t.id}>
                      <button
                        type="button"
                        onClick={() => setOpenTopicId(isOpen ? null : t.id)}
                        className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-accent/40"
                      >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Icon className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{t.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.description}
                          </p>
                        </div>
                        {isOpen ? (
                          <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                        )}
                      </button>
                      {isOpen && (
                        <CardContent className="space-y-3 border-t border-border pt-4 text-sm">
                          {t.body.map((b, i) => (
                            <TopicBlockRenderer key={i} block={b} />
                          ))}
                          {t.link && (
                            <Button asChild variant="outline" size="sm">
                              <Link to={t.link.to}>
                                {t.link.label}
                                <ChevronRight className="size-3" />
                              </Link>
                            </Button>
                          )}
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <HelpCircle className="size-8 text-muted-foreground" />
              <p className="text-sm font-medium">Nenhum tópico encontrado</p>
              <p className="text-xs text-muted-foreground">
                Tente buscar com outras palavras ou veja a seção de Perguntas
                frequentes abaixo.
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* FAQ */}
      <section className="space-y-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HelpCircle className="size-4" />
              Perguntas frequentes
            </CardTitle>
            <CardDescription>
              {query
                ? `${filteredFaq.length} pergunta${filteredFaq.length === 1 ? '' : 's'} ${filteredFaq.length === 1 ? 'corresponde' : 'correspondem'} à sua busca`
                : 'As dúvidas mais comuns dos clientes.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredFaq.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
            {filteredFaq.length === 0 && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                Nenhuma pergunta encontrada para "{query}".
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Atalhos de teclado / Dicas */}
      <section>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lightbulb className="size-4" />
              Dicas para usar melhor
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <TipRow
              icon={KeyRound}
              title="Senhas têm 10 caracteres no mínimo"
              text="Tanto admin quanto motoristas. Use uma combinação de letras, números e símbolos."
            />
            <TipRow
              icon={Smartphone}
              title="No mobile, o veículo é selecionado automaticamente"
              text="Se o motorista tem 1 vínculo ativo, o app já abre com ele. Com 2+, aparece o botão 'TROCAR'."
            />
            <TipRow
              icon={BarChart3}
              title="O dashboard só mostra o que importa"
              text="Cards de 'Atenção' aparecem apenas quando há multas em aberto, manutenção atrasada ou notificações."
            />
            <TipRow
              icon={Bell}
              title="Notificações ficam no sininho"
              text="O sino no topo conta itens não lidos. As regras de alerta são configuradas em Configurações → Alertas."
            />
          </CardContent>
        </Card>
      </section>

      <footer className="border-t border-border pt-4 text-center text-xs text-muted-foreground">
        Não encontrou o que procurava? Mande um e-mail para{' '}
        <a className="text-primary hover:underline" href="mailto:suporte@iabordo.com.br">
          suporte@iabordo.com.br
        </a>{' '}
        com um print da tela e a sequência de passos.
      </footer>
    </div>
  );
}

// ---- Sub-componentes ----------------------------------------------------

function TopicBlockRenderer({ block }: { block: TopicBlock }) {
  if (block.type === 'p') {
    return <p className="text-sm leading-relaxed">{block.text}</p>;
  }
  if (block.type === 'h') {
    return (
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {block.text}
      </p>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul className="space-y-1 pl-4 text-sm">
        {block.items.map((it, i) => (
          <li key={i} className="list-disc">
            {it}
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'ol') {
    return (
      <ol className="space-y-1 pl-4 text-sm">
        {block.items.map((it, i) => (
          <li key={i} className="list-decimal">
            {it}
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === 'tip') {
    return (
      <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
        <span className="font-medium text-warning-foreground">💡 Dica: </span>
        <span>{block.text}</span>
      </div>
    );
  }
  return null;
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-accent/40"
      >
        <div className="mt-0.5">
          {open ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
        </div>
        <p className="flex-1 text-sm font-medium">{q}</p>
      </button>
      {open && (
        <div className="border-t border-border bg-muted/30 p-3 pl-10 text-sm text-muted-foreground">
          {a}
        </div>
      )}
    </div>
  );
}

function TipRow({
  icon: Icon,
  title,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="size-4" />
      </div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}
